import React from 'react';
import { Button } from '@shadcn/components/ui/button';
import { Badge } from '@shadcn/components/ui/badge';

const LocationOrderTable = ({ configs, locationsByRegion, onEdit, onRefresh }) => {
  
  const getRegionColor = (region) => {
    if (!region) return 'bg-gray-100 text-gray-800';
    
    switch (region) {
      case 'AU': return 'bg-blue-100 text-blue-800';
      case 'US': return 'bg-green-100 text-green-800';
      case 'UK': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatLocationOrder = (locationOrder, region) => {
    if (!locationOrder || !Array.isArray(locationOrder)) return 'N/A';
    
    if (locationOrder.length <= 3) {
      return locationOrder.join(', ');
    }
    
    return `${locationOrder.slice(0, 3).join(', ')} +${locationOrder.length - 3} more`;
  };

  const getRegionScope = (config) => {
    if (config.region && config.region !== 'ALL') {
      
      const regionLocations = locationsByRegion?.[config.region]?.length || 0;
      return `${config.region} (${regionLocations} locations)`;
    } else {
      
      const allLocations = Object.values(locationsByRegion || {})
        .flat()
        .map(loc => loc.name);
      const uniqueLocations = [...new Set(allLocations)];
      return `All Regions (${uniqueLocations.length} unique locations)`;
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto border-collapse">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Region Scope</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Type</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Location Order</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Total Locations</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Status</th>
            <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Last Updated</th>
            <th className="px-4 py-3 text-center text-sm font-medium text-gray-900">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {configs.length > 0 ? (
            configs.map((config) => (
              <tr key={config._id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  {config.region && config.region !== 'ALL' ? (
                    <Badge className={getRegionColor(config.region)}>
                      {config.region}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-gray-100 text-gray-800">
                      All Regions
                    </Badge>
                  )}
                  <div className="text-xs text-gray-500 mt-1">
                    {getRegionScope(config)}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {config.type}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  <span title={config.locationOrder?.join(', ')}>
                    {formatLocationOrder(config.locationOrder, config.region)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {config.locationOrder?.length || 0}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={config.isActive ? "default" : "secondary"}>
                    {config.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {new Date(config.updatedAt).toLocaleDateString('en-US')}
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => onEdit(config)}
                    >
                      <i className="bx bx-edit text-sm"></i>
                    </Button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                No configurations found. Please create a new configuration.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default LocationOrderTable; 
